import React from 'react';
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Plus, Wrench } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@shared/ui/accordion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useConsumerRequests } from '../../../requests/hook';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { useTranslation } from 'react-i18next';

export function ServiceRequestsTab({ consumerInfo }: { consumerInfo?: any }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {id}=useParams();
  

  const { data: apiData, isLoading } = useConsumerRequests({
    page: 1,
    limit: 100,
    consumer_id: id,
    remote_utility_id: getRemoteUtilityId(),
    request_type: "Service",
  });

  const requests = React.useMemo(() => {
    if (!apiData?.results || apiData.results.length === 0) return [];
    return apiData.results.map((item: any, index: number) => ({
      keyId: item.id || item.requestNo || `service-${index}`,
      id: item.requestNo || item.id || t("N/A"),
      status: item.statusDisplay || item.status || t("N/A"),
      title: item.requestTypeDisplay || item.requestType || t("N/A"),
      date: item.requestDate || t("N/A"),
      team: item.team || t("Unassigned"),
      scheduled: item.scheduledDate || t("N/A"),
      description: item.consumerRemark || item.description || t("No description provided."),
    }));
  }, [apiData, t]);

  return (
    <div className="py-6">
      <div className="flex justify-end mb-4">
        <Button 
          onClick={() => {
            const params = new URLSearchParams({
              name: consumerInfo?.name || "",
              accountNo: consumerInfo?.accountNo || "",
              address: consumerInfo?.address || "",
              ...(consumerInfo?.selectedConsumerId ? { selectedConsumerId: consumerInfo.selectedConsumerId } : {})
            });
            navigate(`/dashboard/requests/create/service?${params.toString()}`);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm font-bold h-9 px-4 text-xs"
        >
          <Plus className="w-4 h-4 mr-2" /> {t("New Request")}
        </Button>
      </div>
      
      <Accordion type="single" collapsible className="space-y-3">
        {requests.map((req: any) => (
          <AccordionItem 
            key={req.keyId} 
            value={req.keyId} 
            className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
          >
            <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-gray-50/50 transition-colors">
              <div className="flex items-start gap-4 text-left w-full pr-4">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 mt-0.5">
                  <Wrench className="w-4 h-4" />
                </div>
                <div className="flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-bold text-[14px] text-gray-900">{req.id}</span>
                    <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-0 font-bold text-[11px] px-2 py-0.5 rounded-full">
                      {req.status}
                    </Badge>
                  </div>
                  <div className="flex flex-col text-[12px] font-medium text-gray-500">
                    <span>{req.title}</span>
                    <span className="text-gray-400 mt-0.5">{req.date} · {req.team} · {t("Scheduled")}: {req.scheduled}</span>
                  </div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 pt-2 border-t border-gray-100 text-sm text-gray-600 ml-14">
              <p>{req.description}</p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
